import { useState, useRef, useEffect } from 'react';
import { Flex, Text, TextArea, IconButton, Link, ScreenReaderContent, Alert } from '@smarterservices/smarter-ui';
import { IconStarSolid, IconStarLightSolid } from '@smarterservices/smarter-ui';
import { Controller } from 'react-hook-form';

/**
 * RatingInput - A component for collecting rating feedback with optional comments
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.name - Base name for the form field
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.question - Question configuration object
 * @param {number} props.question.minimumValue - Minimum rating value
 * @param {number} props.question.maximumValue - Maximum rating value
 * @param {Object} props.question.question - Question text object
 * @param {boolean} props.required - Whether the rating is required
 * @param {Object} props.error - Error object if validation fails
 * @returns {React.ReactElement} Rating input with optional comment field
 */
const RatingInput = ({ name, control, question, required, error }) => {
    const [showCommentField, setShowCommentField] = useState(false);
    const [ratingRangeArray, setRatingRangeArray] = useState([]);
    const commentRef = useRef(null);
    const commentFieldName = `${name}_comment`;
    const ratingFieldName = `${name}_rating`;
    
    /**
     * Handles the blur event on the comment field
     * 
     * @param {React.FocusEvent} event - The blur event
     */
    const handleCommentBlur = (event) => {
        // if the field has no value, flip the showCommentField state
        if (!event.target.value) {
            setShowCommentField(!showCommentField);
        }
    };
    
    useEffect(() => {
      // create an array of items from the minimumValue and maximumValues provided in the question object...
      const rangeArray = [];
      for (let i = question?.minimumValue; i <= question?.maximumValue; i++) {
        rangeArray.push(i);
      }
      setRatingRangeArray(rangeArray);
    }, [question]);


    const handleAddCommentsClick = () => {
      setShowCommentField(true);
      setTimeout(() => {
        if (commentRef.current) {
          commentRef.current.focus();
        }
      }, 0);
    };
    
    const handleRatingChange = (onChange, value) => {
      onChange(value);
    };

    return (
    <Controller
      name={ratingFieldName}
      control={control}
      rules={{
        required: required ? 'This field is required' : false,
        validate: value => !required || value >= question.minimumValue || 'Please select a rating'
      }}
      render={({ field: { onChange, value, ...field }, fieldState: { error: fieldError } }) => {
        const errorMessage = fieldError?.message || error?.message;
        return (
          <Flex direction="column" gap="x-small">
            <Flex direction="column" gap="x-small">
              {errorMessage && (
                <Alert variant="error" title="Error">
                  {errorMessage}
                </Alert>
              )}
              <Text as="div" size="large">
                {question.question?.en_US || 'Rate your experience'}
                {required && <Text color="danger" size="x-large">*</Text>}
              </Text>  
              </Flex>
            <Flex>
              {ratingRangeArray.map((rating) => (
                <IconButton
                  key={rating}
                  withBackground={false}
                  withBorder={false}
                  onClick={() => handleRatingChange(onChange, rating)}
                  screenReaderLabel={`Set rating as ${rating} out of ${question?.maximumValue}`}
                >
                  {rating <= value ? (
                    <IconStarSolid color={errorMessage ? 'danger' : 'success'} />
                  ) : (
                    <IconStarLightSolid color={errorMessage ? 'danger' : 'secondary'} />
                  )}
                </IconButton>
              ))}
            </Flex>
            
            <Flex direction="column" gap="small">
                <input
                  type="hidden"
                  {...field}
                  value={value || ''}
                />
                
                {showCommentField && (
                  <Controller
                    name={commentFieldName}
                    control={control}
                    render={({ field: commentField }) => (
                      <TextArea
                        {...commentField}
                        inputRef={(el) => {
                          commentRef.current = el?.inputRef?.current || el;
                          commentField.ref(el);
                        }}
                        autoFocus
                        label={<ScreenReaderContent>Additional Comments</ScreenReaderContent>}
                        placeholder="Please share any additional comments regarding your rating..."
                        maxHeight="10rem"
                        margin="medium 0 0 0"
                        onBlur={handleCommentBlur}
                      />
                    )}
                  />
                )}
                
                {!showCommentField && (
                  <Link
                    variant="link"
                    onClick={handleAddCommentsClick}
                    screenReaderLabel="Add additional comments"
                  >
                    Add additional feedback
                  </Link>
                )}
              </Flex>
            
          </Flex>
        );
      }}
    />
  )
};

export default RatingInput;
